import { CircularProgress, Collapse, Divider, Stack } from "@mui/material";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { QUALIFICATION_WITHOUT_CHARGE_TOOLTIP } from "@src/appV2/Shifts/utils/getQualificationsAvailableToDisplay";

import { LoadingButton } from "../../../components/LoadingButton";
import { Text } from "../../../components/Text";
import { type SettingsPermissions } from "../../types";
import { type WorkerTypeItem, WorkerTypeRowItem } from "./WorkerTypeRowItem";

interface CalendarDisplaySectionViewProps {
  isDirty: boolean;
  isLoading: boolean;
  isLoadingShiftCounts: boolean;
  isUpdating: boolean;
  permissions: SettingsPermissions;
  workerTypes: WorkerTypeItem[];
  onMoveDown: (index: number) => void;
  onMoveUp: (index: number) => void;
  onSave: () => void;
  onVisibilityToggle: (index: number, checked: boolean) => void;
}

function SectionLabel(properties: { children: string }) {
  return (
    <Text
      semibold
      sx={(theme) => ({
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: "uppercase",
      })}
      variant="caption"
    >
      {properties.children}
    </Text>
  );
}

export function CalendarDisplaySectionView({
  isDirty,
  isLoading,
  isLoadingShiftCounts,
  isUpdating,
  permissions,
  workerTypes,
  onMoveDown,
  onMoveUp,
  onSave,
  onVisibilityToggle,
}: CalendarDisplaySectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={(theme) => ({ padding: theme.spacing(4) })}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (workerTypes.length === 0) {
    return (
      <Text
        sx={(theme) => ({
          color: theme.palette.text.disabled,
          fontStyle: "italic",
          padding: `${theme.spacing(3)} 0`,
        })}
        variant="body2"
      >
        {tu("No professional types configured for this workplace.")}
      </Text>
    );
  }

  const hasHiddenTypes = workerTypes.some((wt) => !wt.visible);

  return (
    <Stack spacing={2}>
      <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
        {permissions.isAdmin
          ? tu("Show or hide professional types on the calendar. Use arrows to reorder.")
          : tu(
              "Only administrators can change these settings. Contact Clipboard support to request changes."
            )}
      </Text>

      <Stack spacing={0}>
        <SectionLabel>{tu("Visible on calendar")}</SectionLabel>
        <Stack spacing={0}>
          {workerTypes.map((workerType, index) => (
            <Collapse key={workerType.name} unmountOnExit in={workerType.visible} timeout={250}>
              <WorkerTypeRowItem
                disabledTooltip={
                  workerType.isWithoutCharge ? QUALIFICATION_WITHOUT_CHARGE_TOOLTIP : undefined
                }
                hasActiveShifts={workerType.hasActiveShifts}
                index={index}
                isAdmin={permissions.isAdmin}
                isLoadingShiftCounts={isLoadingShiftCounts}
                isUpdating={isUpdating}
                totalCount={workerTypes.length}
                workerType={workerType}
                onMoveDown={onMoveDown}
                onMoveUp={onMoveUp}
                onVisibilityToggle={onVisibilityToggle}
              />
            </Collapse>
          ))}
        </Stack>

        <Collapse in={hasHiddenTypes} timeout={250}>
          <Divider sx={{ marginY: 2 }} />
          <SectionLabel>{tu("Hidden")}</SectionLabel>
          <Stack spacing={0}>
            {workerTypes.map((workerType, index) => (
              <Collapse key={workerType.name} unmountOnExit in={!workerType.visible} timeout={250}>
                <WorkerTypeRowItem
                  disabledTooltip={
                    workerType.isWithoutCharge ? QUALIFICATION_WITHOUT_CHARGE_TOOLTIP : undefined
                  }
                  hasActiveShifts={workerType.hasActiveShifts}
                  index={index}
                  isAdmin={permissions.isAdmin}
                  isLoadingShiftCounts={isLoadingShiftCounts}
                  isUpdating={isUpdating}
                  totalCount={workerTypes.length}
                  workerType={workerType}
                  onMoveDown={onMoveDown}
                  onMoveUp={onMoveUp}
                  onVisibilityToggle={onVisibilityToggle}
                />
              </Collapse>
            ))}
          </Stack>
        </Collapse>
      </Stack>

      {permissions.isAdmin && (
        <Stack alignItems="flex-start" sx={{ paddingTop: 1 }}>
          <LoadingButton
            disabled={!isDirty}
            isLoading={isUpdating}
            variant="contained"
            onClick={onSave}
          >
            {tu("Save Changes")}
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
}
